import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Injector, Input, OnChanges, OnDestroy, ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  NG_VALUE_ACCESSOR, ValidatorFn, Validators
} from '@angular/forms';
import {
  debounceTime, filter, fromEvent, map, Observable, Subject, switchMap, takeUntil, tap
} from 'rxjs';

import { BaseControlValueAccessor, IIrisFormFieldControl } from '@sondermind/iris/form-helpers';
import { IconColors, IconSizes, IconUsageTypes } from '@sondermind/iris/icon';

import {
  AlphabeticKeys, EventActions, NavigationKeys, NUMERIC_KEYS
} from './event-keys';
import { DropdownOption } from './interfaces/dropdown-option.interface';
import { IrisDropdownType } from './enums/dropdown-type.enum';

export const enum MultiSelectMaxHeight {
  LINE_HEIGHT_1 = 44,
  LINE_HEIGHT_3 = 115
}

@Component({
  selector: 'iris-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: IrisDropdownComponent,
    },
  ],
  standalone: false
})
export class IrisDropdownComponent<T>
  extends BaseControlValueAccessor
  implements OnDestroy, IIrisFormFieldControl, OnChanges {
  constructor(injector: Injector, private cdRef: ChangeDetectorRef) { super(injector) }

  @Input() placeholder: string = '';
  @Input() options: Array<DropdownOption<T>> = [];
  @Input() multiple: boolean = false;
  @Input() maxOptions: number = -1;
  @Input() canSelectAll: boolean = false;
  @Input() canAutoResize: boolean = true;
  @Input() dropdownType: IrisDropdownType = IrisDropdownType.SELECT;
  @Input() resetDropdownSelection: boolean = false;
  @Input() optionDataTestAttributeName?: string;
  @ViewChild('dropdown') dropdown!: ElementRef;
  @ViewChild('listbox') listbox!: ElementRef;
  @ViewChild('hiddenContainer') hiddenContainer!: ElementRef;

  currentSelection: DropdownOption<T> | null = null; // For single selection mode
  currentSelections: Array<DropdownOption<T>> = []; // For multiple selection mode
  currentFocusedValue: T | 'select-all' | null = null;
  currentHoveredValue: T | 'select-all' | null = null;
  dropdownFilterType = IrisDropdownType.FILTER;
  iconSizes = IconSizes;
  iconUsageTypes = IconUsageTypes;
  iconColors = IconColors;

  // Handles position of listbox overlay
  shouldDropUp: boolean = false;

  // Handles expanded/collapsed state of menu
  isExpanded: boolean = false;

  // Handles when the "Select all" option is checked or indeterminate
  selectAllToggleActive: boolean = false;
  selectAllToggleFocused: boolean = false;

  // Sets max-height and calculates overflow for multi-select
  maxHeight: MultiSelectMaxHeight = MultiSelectMaxHeight.LINE_HEIGHT_3;
  hiddenTags: Array<DropdownOption<T>> = [];
  overflowTags: Array<DropdownOption<T>> = [];
  visibleTags: Array<DropdownOption<T>> = [];
  resizeObserver?: ResizeObserver;

  // Handles typeahead functionality
  destroyed$ = new Subject<void>();
  focusedElement$ = new Subject<HTMLElement>();

  ngOnChanges(): void {
    if (!this.multiple) {
      this.initSingleSelect();
    } else {
      this.initMultiSelect();
    }
  }
  ngAfterViewInit(): void {
    if (!this.multiple) {
      this.enableTypeahead();
    } else {
      (this.dropdown.nativeElement as HTMLElement).style.maxHeight = `${this.maxHeight}px`;
      this.createResizeObserver();
    }

    if (this.currentSelections.length) {
      this.renderItemTags();
    }
  }

  private initSingleSelect(): void {
    // Adds an option to the list that will reset the current selection to null
    const isRequired = this.control.hasValidator(Validators.required) ||
    (this.control.validator && (() => {
      const testControl = new FormControl(null);
      // Set the parent so validators can access sibling controls if needed
      testControl.setParent(this.control.parent);
      const validationResult = this.control.validator(testControl);
      return Boolean(validationResult?.['required']);
    })());

    if (!isRequired && this.options?.length > 0) {
      this.options = [{ value: null as T, name: this.placeholder }, ...this.options];
    }

    const isInitialValueExists =
      this.control.value !== undefined
      && this.control.value !== null
      && this.control.value !== '';

    // Default to placeholder text if the FormControl didn't initialize a value
    if (isInitialValueExists) {
      this.currentSelection = this.options
        ?.find((option) => option.value === this.control.value)
        ?? null;
    } else {
      this.control.setValue(null);
    }

    // Listen to FormControl in case it gets updated programmatically
    this.listenForSingleValueChange();
    this.listenForStatusChange();
  }

  private listenForSingleValueChange(): void {
    this.control.valueChanges.pipe(
      takeUntil(this.destroyed$),
      tap((newValue: T) => {
        const option = this.options?.find((opt) => opt.value === newValue);
        if (option) {
          this.changeSelection(option);
        } else {
          this.currentSelection = null;
          if (this.isExpanded) {
            this.collapseMenu();
            this.redirectFocus(this.dropdown.nativeElement);
          }
        }
      })
    ).subscribe();
  }

  private listenForStatusChange(): void {
    this.control.statusChanges.pipe(
      takeUntil(this.destroyed$),
      tap(() => this.cdRef.markForCheck())
    ).subscribe();
  }

  private enableTypeahead(): void {
    this.focusedElement$.pipe(
      switchMap((elem) => this.getSearchTerm$(elem)),
      tap((term) => this.getSearchResults(term)),
      takeUntil(this.destroyed$)
    ).subscribe();

    this.focusedElement$.next(this.dropdown.nativeElement);
  }

  private initMultiSelect(): void {
    const controlValues = this.control.value as T[];
    const initialValueExists = controlValues.length;

    if (initialValueExists) {
      this.setInitialValues();
      this.canSelectAll && controlValues.length === this.options.length
        ? this.selectAllToggleActive = true
        : this.selectAllToggleActive = false;
    } else {
      this.control.setValue([]);
    }

    if (this.maxOptions >= 2) {
      this.addMaxOptionsValidator();
    }

    if (!this.canAutoResize) {
      this.maxHeight = MultiSelectMaxHeight.LINE_HEIGHT_1;
    }

    this.listenForMultiValueChange();
    this.listenForStatusChange();
  }

  private setInitialValues(): void {
    // If an invalid value is passed during initialization, remove it
    const validInitialValues: T[] = [];
    this.options.forEach((option) => {
      const controlValues = this.control.value as T[];
      if (controlValues.includes(option.value)) {
        validInitialValues.push(option.value);
        this.currentSelections.push(option);
      }
    });

    this.control.setValue(validInitialValues);
  }

  private addMaxOptionsValidator(): void {
    const maxOptionsValidator: ValidatorFn = ((control: AbstractControl) => (control?.value?.length > this.maxOptions
      ? { maxOptions: true }
      : null)) as ValidatorFn;

    this.control.addValidators(maxOptionsValidator);
  }

  private createResizeObserver(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(() => {
        const isOverMaxHeight = (this.hiddenContainer.nativeElement as HTMLElement).offsetHeight > this.maxHeight;
        if (isOverMaxHeight) {
          this.cdRef.detach();
          this.detectOverflow();
        }
      });
    });

    this.resizeObserver.observe(document.body);
  }

  private listenForMultiValueChange(): void {
    this.control.valueChanges.pipe(
      takeUntil(this.destroyed$),
      tap((newValue: T[]) => {
        this.setMultiSelections(newValue);
        this.renderItemTags();
        if (this.canSelectAll) {
          this.setSelectAllToggleState(newValue);
        }
      })
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onHoverEnter(value: T | 'select-all'): void {
    this.currentHoveredValue = value;
  }

  onHoverLeave(): void {
    this.currentHoveredValue = null;
  }

  isKeyboardEvent(event: Event): event is KeyboardEvent {
    return event instanceof KeyboardEvent;
  }

  getSearchTerm$(targetElem: HTMLElement): Observable<string> {
    let term = '';

    return fromEvent<KeyboardEvent>(targetElem, 'keydown').pipe(
      filter(this.isKeyboardEvent),
      filter((e: KeyboardEvent) => Object.keys(AlphabeticKeys).includes(e.key)
        || Object.keys(NUMERIC_KEYS).includes(e.key)),
      map((e: KeyboardEvent) => {
        term += e.key;
        return term;
      }),
      debounceTime(250),
      // eslint-disable-next-line no-return-assign
      tap(() => term = ''),
      takeUntil(this.destroyed$)
    );
  }

  onBlur(e: FocusEvent): void {
    const elemReceivingFocus = e.relatedTarget as HTMLElement;

    // When the menu is expanded, a click outside of the dropdown list closes the menu
    if (elemReceivingFocus) {
      const isInsideClick = elemReceivingFocus.classList.contains('iris-dropdown-list-item')
        || elemReceivingFocus.classList.contains('iris-checkbox')
        || elemReceivingFocus.classList.contains('iris-checkbox-box')
        || elemReceivingFocus.classList.contains('iris-indeterminate')
        || elemReceivingFocus.classList.contains('iris-checkmark')
        || elemReceivingFocus.classList.contains('iris-checkmark-half');

      if (!isInsideClick) {
        this.closeOnBlur();
      }
    } else {
      this.closeOnBlur();
    }
  }

  closeOnBlur(): void {
    this.control.markAsTouched();
    this.collapseMenu();
  }

  expandClickMenu(e: MouseEvent): void {
    e.stopImmediatePropagation();
    this.expandMenu();

    if (this.control.value) {
      this.focusCurrentSelection();
    }
  }

  expandKeyboardMenu(e: KeyboardEvent): void {
    e.stopImmediatePropagation();
    this.expandMenu();

    if (this.control.value) {
      this.focusCurrentSelection();
    } else {
      this.focusListItem(0);
    }
  }

  expandMenu(): void {
    this.isExpanded = true;
    this.shouldDropUp = this.getDropdownPosition();
  }

  getDropdownPosition(): boolean {
    const position = (this.dropdown.nativeElement as HTMLElement).getBoundingClientRect();
    const viewportCenter = window.innerHeight / 2;

    return position.y > viewportCenter;
  }

  collapseMenu(): void {
    this.currentFocusedValue = null;
    this.isExpanded = false;
  }

  changeSelection(option: DropdownOption<T>): void {
    this.currentSelection = option;
    this.updateFormControl(option.value);

    if (this.isExpanded) {
      this.collapseMenu();
      this.redirectFocus(this.dropdown.nativeElement);
    }
  }

  updateFormControl(newValue: T): void {
    if (this.control.value !== newValue) {
      this.control.setValue(newValue);
    } else if (this.resetDropdownSelection) {
      // If the same value is selected again, reset the form control to allow re-selection.
      // Clears the current selection to allow the same option to be selected again.
      this.control.reset(null, { emitEvent: false });
    }
    this.control.markAsTouched();
    this.control.markAsDirty();
  }

  redirectFocus(elem: HTMLElement | null): void {
    if (!elem) return;
    elem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    elem.focus();

    this.focusedElement$.next(elem);
  }

  focusCurrentSelection(): void {
    const currentItemIndex: number = this.options.findIndex((option) => option.value === this.control.value);
    this.focusListItem(currentItemIndex);
  }

  focusListItem(index: number): void {
    if (this.multiple && this.canSelectAll) {
      this.currentFocusedValue = index ? this.options[index - 1].value : 'select-all';
    } else {
      this.currentFocusedValue = this.options[index]?.value;
    }

    // Ensures that focus changes only after the hidden class is removed from the listbox
    const divToFocus = (this.listbox.nativeElement as HTMLElement).children[index];
    setTimeout(() => this.redirectFocus(divToFocus as HTMLElement), 0);
  }

  navigateList(direction: NavigationKeys.ArrowDown | NavigationKeys.ArrowUp, action: EventActions): void {
    const currentItemIndex: number = this.isExpanded
      ? this.options.findIndex((option) => option.value === this.currentFocusedValue)
      : this.options.findIndex((option) => option.value === this.control.value);
    let nextItemIndex: number = 0;
    let isEndOfList = true;

    if (direction === NavigationKeys.ArrowDown && currentItemIndex < this.options.length - 1) {
      isEndOfList = false;
      nextItemIndex = currentItemIndex + 1;
    } else if (direction === NavigationKeys.ArrowUp && currentItemIndex > 0) {
      isEndOfList = false;
      nextItemIndex = currentItemIndex - 1;
    }

    if (!isEndOfList && action === EventActions.Focus) {
      const nextItemElem = (this.listbox.nativeElement as HTMLElement).children[nextItemIndex];
      this.redirectFocus(nextItemElem as HTMLElement);
      this.currentFocusedValue = this.options[nextItemIndex].value;
    } else if (!isEndOfList && action === EventActions.Select) {
      const nextOption = this.options[nextItemIndex];
      this.changeSelection(nextOption);
    }
  }

  escapeMenu(e: Event): void {
    e.stopPropagation();
    this.collapseMenu();
    this.redirectFocus(this.dropdown.nativeElement);
  }

  handleKeyDown(e: KeyboardEvent): void {
    if (Object.keys(NavigationKeys).includes(e.key)) {
      this.handleKeyNavigation(e);
    }
  }

  handleKeyNavigation(e: KeyboardEvent): void {
    switch (e.key) {
      case NavigationKeys.Tab:
        if (this.isExpanded) {
          this.collapseMenu();
        }
        break;
      case NavigationKeys.Enter:
      case NavigationKeys[' ']:
        if (this.isExpanded) {
          e.preventDefault();
          const option = this.options.find((opt) => opt.value === this.currentFocusedValue);
          if (option) {
            this.changeSelection(option);
          }
        } else {
          this.expandKeyboardMenu(e);
        }
        break;
      case NavigationKeys.Escape:
        // Prevents browser window minimization on keydown, while menu collapse is
        // handled on keyup to prevent event from bubbling up to modals
        e.preventDefault();
        break;
      case NavigationKeys.Home:
        if (this.isExpanded) {
          e.preventDefault();
          this.focusListItem(0);
        }
        break;
      case NavigationKeys.End:
        if (this.isExpanded) {
          e.preventDefault();
          this.focusListItem(this.options.length - 1);
        }
        break;
      case NavigationKeys.ArrowUp:
        e.preventDefault();
        this.isExpanded
          ? this.navigateList(NavigationKeys.ArrowUp, EventActions.Focus)
          : this.navigateList(NavigationKeys.ArrowUp, EventActions.Select);
        break;
      case NavigationKeys.ArrowDown:
        e.preventDefault();
        this.isExpanded
          ? this.navigateList(NavigationKeys.ArrowDown, EventActions.Focus)
          : this.navigateList(NavigationKeys.ArrowDown, EventActions.Select);
        break;
      default:
    }
  }

  getSearchResults(term: string): void {
    const match = this.findMatch(term);

    if (match && this.isExpanded) {
      const optionIndex: number = this.options.findIndex((option) => option.value === match.value);
      this.focusListItem(optionIndex);
    } else if (match && !this.isExpanded) {
      this.changeSelection(match);
      this.cdRef.markForCheck();
    }
  }

  findMatch(term: string): DropdownOption<T> | undefined {
    let counter = 0;
    let copy = this.options;
    let filteredOptions: Array<DropdownOption<T>> = [];

    if (term) {
      while (counter < term.length) {
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        filteredOptions = copy.filter((opt) => opt
          .name
          .toLowerCase()[counter] === term.toLowerCase()[counter]
        );

        if (filteredOptions.length) {
          copy = filteredOptions;
          // eslint-disable-next-line no-plusplus
          counter++;
        } else if (!filteredOptions.length && !counter) {
          // eslint-disable-next-line consistent-return
          return undefined;
        } else {
          // eslint-disable-next-line consistent-return
          return copy[0];
        }
      }
    }

    if (filteredOptions?.length) {
      // eslint-disable-next-line consistent-return
      return filteredOptions[0];
    }
    return undefined;
  }

  // Functions for multiple selection mode below
  expandClickMultiMenu(e: MouseEvent): void {
    e.stopImmediatePropagation();
    const targetElem = e.target as HTMLElement;
    const isTagClicked = targetElem.classList.contains('iris-dropdown-item-tag');
    const isDeleteIconClicked = targetElem.classList.contains('delete-icon');

    // Need to focus on the element since safari doesn't automatically do that
    document.getElementById('iris-dropdown-button')?.focus();

    if (!isTagClicked && !isDeleteIconClicked) {
      this.expandMenu();
    }
  }

  expandKeyboardMultiMenu(e: KeyboardEvent): void {
    e.stopImmediatePropagation();
    this.expandMenu();

    if ((this.control.value as T[]).length && !this.selectAllToggleActive) {
      this.focusLastSelection();
    } else if (this.canSelectAll) {
      this.selectAllToggleFocused = true;
      this.focusListItem(0);
    } else {
      this.focusListItem(0);
    }
  }

  onTagClick(tag: DropdownOption<T>): void {
    if (!this.control.disabled) {
      this.changeMultiSelection(tag);
    }
  }

  focusLastSelection(): void {
    const controlValues = this.control.value as T[];
    const lastSelectionIndex = controlValues.length - 1;
    const currentItemIndex: number = this.options
      .findIndex((option) => option.value === controlValues[lastSelectionIndex]);
    this.canSelectAll ? this.focusListItem(currentItemIndex + 1) : this.focusListItem(currentItemIndex);
  }

  changeMultiSelection(option: DropdownOption<T>): void {
    if (!this.currentSelections.includes(option)) {
      this.currentSelections.push(option);
    } else {
      this.currentSelections = this.currentSelections.filter((opt) => opt !== option);
    }
    this.updateMultiFormControl(option.value);
    this.renderItemTags();
  }

  updateMultiFormControl(newValue: T): void {
    const controlValues = this.control.value as T[];

    if (!controlValues.includes(newValue)) {
      this.control.setValue([...controlValues, newValue]);
    } else {
      const newSelections = controlValues.filter((val: T) => val !== newValue);
      this.control.setValue(newSelections);
    }
    this.control.markAsTouched();
    this.control.markAsDirty();
  }

  setMultiSelections(newValues: T[]): void {
    const foundOptions: Array<DropdownOption<T>> = [];
    newValues.forEach((value) => {
      const foundOption = this.options.find((option) => option.value === value);
      if (foundOption) {
        foundOptions.push(foundOption);
      }
    });

    this.currentSelections = foundOptions;
  }

  handleMultiKeyDown(e: KeyboardEvent): void {
    if (Object.keys(NavigationKeys).includes(e.key)) {
      this.isExpanded ? this.handleMultiKeyNavigation(e) : this.handleMultiMenuOpen(e);
    }
  }

  handleMultiMenuOpen(e: KeyboardEvent): void {
    if (e.key === NavigationKeys.Enter || e.key === NavigationKeys[' ']) {
      this.expandKeyboardMultiMenu(e);
    }
  }

  handleMultiKeyNavigation(e: KeyboardEvent): void {
    switch (e.key) {
      case NavigationKeys.Tab:
        this.collapseMenu();
        break;
      case NavigationKeys.Enter:
      case NavigationKeys[' ']:
        e.preventDefault();
        this.handleMultiSelection();
        break;
      case NavigationKeys.Escape:
        // Prevents browser window minimization on keydown, while menu collapse is
        // handled on keyup to prevent event from bubbling up to modals
        e.preventDefault();
        break;
      case NavigationKeys.Home:
        e.preventDefault();
        this.focusListItem(0);
        this.selectAllToggleFocused = this.canSelectAll;
        break;
      case NavigationKeys.End:
        e.preventDefault();
        // List end is offset by 1 if a "Select all" option is present
        this.canSelectAll
          ? this.focusListItem(this.options.length)
          : this.focusListItem(this.options.length - 1);
        this.selectAllToggleFocused = false;
        break;
      case NavigationKeys.ArrowUp:
        e.preventDefault();
        this.canSelectAll
          ? this.navigateSelectAllList(NavigationKeys.ArrowUp)
          : this.navigateList(NavigationKeys.ArrowUp, EventActions.Focus);
        break;
      case NavigationKeys.ArrowDown:
        e.preventDefault();
        this.canSelectAll
          ? this.navigateSelectAllList(NavigationKeys.ArrowDown)
          : this.navigateList(NavigationKeys.ArrowDown, EventActions.Focus);
        break;
      default:
    }
  }

  handleMultiSelection(): void {
    if (this.selectAllToggleFocused) {
      this.toggleSelectAll();
    } else if (this.currentFocusedValue !== null) {
      const option = this.options.find((opt) => opt.value === this.currentFocusedValue);
      if (option) {
        this.changeMultiSelection(option);
      }
    }
  }

  // Handles list navigation when a "Select all" option is present in the menu
  navigateSelectAllList(direction: NavigationKeys.ArrowDown | NavigationKeys.ArrowUp): void {
    // The current option in focus - corresponds to its index in the options list
    let currentItemIndex: number | 'select-all' | undefined;
    // The next option to focus - if null, focus the "Select all" option; if undefined, end of list
    let nextItemIndex: number | 'select-all' | undefined;
    let isEndOfList = false; // Whether focus is on the first or last element in the list

    if (this.selectAllToggleFocused) {
      currentItemIndex = 'select-all';
    } else if (!this.selectAllToggleFocused && this.currentFocusedValue !== null) {
      currentItemIndex = this.options.findIndex((option) => option.value === this.currentFocusedValue);
    }

    if (currentItemIndex === undefined && direction === NavigationKeys.ArrowDown) {
      nextItemIndex = 'select-all';
    } else if (currentItemIndex === 'select-all' && direction === NavigationKeys.ArrowDown) {
      nextItemIndex = 0;
    } else if (currentItemIndex !== 'select-all' && direction === NavigationKeys.ArrowUp && currentItemIndex === 0) {
      nextItemIndex = 'select-all';
    } else if (currentItemIndex !== undefined) {
      if (currentItemIndex !== 'select-all' && direction === NavigationKeys.ArrowUp && currentItemIndex > 0) {
        nextItemIndex = currentItemIndex - 1;
      } else if (
        currentItemIndex !== 'select-all'
        && direction === NavigationKeys.ArrowDown
        && currentItemIndex < this.options.length - 1
      ) {
        nextItemIndex = currentItemIndex + 1;
      }
    }

    if (nextItemIndex === undefined) {
      isEndOfList = true;
    }

    const listboxChildren = (this.listbox.nativeElement as HTMLElement).children;
    if (!isEndOfList && nextItemIndex === 'select-all') {
      this.selectAllToggleFocused = true;
      this.currentFocusedValue = 'select-all';
      this.redirectFocus(listboxChildren[0] as HTMLElement);
    } else if (!isEndOfList && typeof nextItemIndex === 'number' && nextItemIndex >= 0) {
      this.selectAllToggleFocused = false;
      this.currentFocusedValue = this.options[nextItemIndex].value;
      this.redirectFocus(listboxChildren[nextItemIndex + 1] as HTMLElement);
    }
  }

  setSelectAllToggleState(newValue: T[]): void {
    // Prevents the select-all checkbox from entering an indeterminate state
    // when the form control value is cleared programmatically
    if (!newValue.length) {
      this.selectAllToggleActive = false;
    }

    if (newValue.length === this.options.length) {
      this.selectAllToggleActive = true;
    }
  }

  toggleSelectAll(): void {
    this.selectAllToggleActive ? this.deselectAll() : this.selectAll();
  }

  selectAll(): void {
    this.selectAllToggleActive = true;
    this.options.forEach((option) => {
      if (!this.currentSelections.includes(option)) {
        this.currentSelections.push(option);
        this.updateMultiFormControl(option.value);
      }
    });
    this.renderItemTags();
  }

  deselectAll(): void {
    this.selectAllToggleActive = false;
    this.currentSelections = [];
    this.hiddenTags = [];
    this.visibleTags = [];
    this.overflowTags = [];
    this.control.setValue([]);
  }

  renderItemTags(): void {
    this.hiddenTags = [...this.currentSelections];
    this.overflowTags = [];

    setTimeout(() => {
      this.cdRef.detach();
      this.detectOverflow();
    }, 0);
  }

  detectOverflow(): void {
    const hiddenContainerElement = this.hiddenContainer.nativeElement as HTMLElement;
    if (hiddenContainerElement.offsetHeight === 0) {
      this.cdRef.detectChanges();
    }

    const isOverMaxHeight = hiddenContainerElement.offsetHeight > this.maxHeight;

    if (isOverMaxHeight && this.hiddenTags.length > 1) {
      const tagToHide = this.hiddenTags.pop() ;
      if (tagToHide) {
        this.overflowTags.unshift(tagToHide);
      }

      this.cdRef.detectChanges();
      this.detectOverflow();
    } else {
      this.visibleTags = this.hiddenTags;
      this.cdRef.detectChanges();
      this.cdRef.reattach();
    }
  }
}
